/**
 * HealthAgent — Tier 0 slice-local HONEST PLACEHOLDER
 * (epic SCRUM-332 / SCRUM-333, cross-cutting).
 *
 * The reservations bundle sketches an "AI Health Agent" that rolls four
 * operational checks into a Healthy / Needs-attention / At-risk verdict
 * on the reservation detail header
 * (`design_handoff_reservations/COMPONENT_API.md` §`HealthAgent`, README
 * rows 16/49/65). This component renders the four check CATEGORIES but
 * DELIBERATELY FABRICATES NO VERDICT — because no health-signal source
 * exists yet.
 *
 * **Why no verdict / no data (epic NEEDs).** Every input the agent would
 * consume is UNBUILT:
 *   - Payment            → NEEDs the Payments BC folio/schedule signal
 *                          (money-IN; not yet wired to the FE).
 *   - Arrival checklist  → NEEDs an arrival/check-in signal — no
 *                          housekeeping/inspection/messaging BC exists.
 *   - Checkout audit     → NEEDs a checkout-audit signal — same missing BCs.
 *   - Housekeeping       → NEEDs a housekeeping BC — does not exist.
 * With no source, ANY green/yellow/red dot or "Healthy" chip would be
 * fabricated. Per SCRUM-333 ("HealthAgent fabricates nothing") and the
 * standing "never fake data" rule, this renders an explicit "signals not
 * connected yet" state instead. It takes NO `Reservation` health input —
 * props stay minimal so no caller can smuggle in mock verdicts.
 *
 * **Tokens (ADR frontend-0003).** Semantic `--fg-*` / `--bg-*` tokens only;
 * no hex, no bare inline numerics (SCRUM-78/79). State is carried by TEXT
 * (the "Not connected yet" caption per category), never color alone
 * (WCAG 2.1 AA, FE rule 7).
 *
 * **Tier (ADR frontend-0004).** Tier 0: slice-local, NOT barrel-exported.
 * When the real signals land (Payments FE hook + a housekeeping/arrival
 * signal port), a follow-up ticket replaces the honest placeholder with a
 * live agent and the second-consumer trigger governs any promotion.
 */

import * as React from 'react';
import { Card, Icon, type IconName } from '@sparta/ui-kit';

/** The four operational check categories the agent will eventually cover.
 *  Rendered as labelled rows with an honest "not connected yet" caption —
 *  no per-category verdict, because no signal source exists. */
interface HealthCategory {
  readonly label: string;
  /** Decorative leading glyph (registered `IconName`). */
  readonly icon: IconName;
}

const HEALTH_CATEGORIES: readonly HealthCategory[] = [
  { label: 'Payment', icon: 'receipt' },
  { label: 'Arrival checklist', icon: 'check' },
  { label: 'Checkout audit', icon: 'clock' },
  { label: 'Housekeeping', icon: 'bed' },
];

let nextHeadingId = 0;

export function HealthAgent(): React.ReactElement {
  // Pin a stable id once per mount for the region's accessible name.
  const [headingId] = React.useState(() => `booking-health-agent-heading-${nextHeadingId++}`);

  return (
    <Card asChild variant="sunken" padding="md">
      <section aria-labelledby={headingId}>
        <div className="mb-2 flex items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-flex items-center text-[color:var(--fg-tertiary)]"
          >
            <Icon name="sparkles" size={16} />
          </span>
          <h2 id={headingId} className="text-sm font-semibold text-[color:var(--fg-primary)]">
            Health agent
          </h2>
        </div>
        <p className="mb-3 text-xs text-[color:var(--fg-secondary)]">
          Signals not connected yet — payment, arrival, checkout, and housekeeping sources are not
          wired.
        </p>
        <ul className="flex flex-col gap-2">
          {HEALTH_CATEGORIES.map((category) => (
            <li key={category.label} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className="inline-flex items-center text-[color:var(--fg-tertiary)]"
                >
                  <Icon name={category.icon} size={14} />
                </span>
                <span className="text-sm text-[color:var(--fg-primary)]">{category.label}</span>
              </span>
              {/* Honest, non-color-only status: the words carry the state. */}
              <span className="text-xs text-[color:var(--fg-tertiary)]">Not connected yet</span>
            </li>
          ))}
        </ul>
      </section>
    </Card>
  );
}
